import { Button, Input } from "@mantine/core";
import {
  Avatar,
  Checkbox,
  Collapse,
  ConfigProvider,
  DatePicker,
  Modal,
  Progress,
  QRCode,
  Select,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { Handle, Position } from "@xyflow/react";
import { IconMail, IconPencil, IconPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";

import DragDropList from "@/components/DragDropList";
import WorkItemDialog from "@/components/WorkItemDialog";
import type { WorkItemDialogValue } from "@/components/WorkItemDialog";
import HistoryModal from "./ProductionResumeTable/HistoryModal";
import { useProjectIfc3d, assemblyMarks } from "./useProjectIfc3d";
import type {
  HistoryRecord,
  ReportStatus,
  WorkTask,
} from "./ProductionResumeTable/data";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useParams } from "react-router-dom";
import { useSelection } from "@/contexts/SelectionContext";
import { useTranslation } from "react-i18next";
import "./ProductionResumeTable/styles.css";

const NODE_TYPE_LABEL: Record<string, string> = {
  building: "建築本體",
  zone: "分區",
  batch: "分批",
  package: "構件包",
};

interface TextUpdaterNodeProps {
  data: {
    label: string;
    name: string;
    type: string;
    documentId?: string;
    childNodes?: string[];
    workItem?: any[];
    steelAssemblyList?: any[];
    steelAssemblyStats?: Record<string, any>;
    priority?: number;
  };
  isConnectable: boolean;
  id: string;
  type: string;
  onNodeUpdate?: (nodeId: string, newName: string) => void;
  borderColor?: string;
  showQRCode?: boolean;
  selected?: boolean;
}

function TextUpdaterNode({
  data,
  isConnectable,
  id,
  type,
  onNodeUpdate,
  borderColor = "#d1d5db",
  showQRCode = false,
  selected = false,
}: TextUpdaterNodeProps) {
  const { t } = useTranslation();
  const {
    selectionState,
    setSelectedBOMRows,
    setModalFilters,
    setModalCurrentPage,
    setModalPageSize,
  } = useSelection();

  const [isModelOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{ title?: string }>({});
  const [isAddSteelAssembly, setIsAddSteelAssembly] = useState(false);
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [materialBomData, setMaterialBomData] = useState([]);
  const [bomDataCache, setBomDataCache] = useState<Record<string, any>>({});
  const [steelAssemblyList, setSteelAssemblyList] = useState<any[]>([]);
  const [steelAssemblyStats, setSteelAssemblyStats] = useState<
    Record<string, any>
  >({});
  const [steelAssemblyDetails, setSteelAssemblyDetails] = useState<any[]>([]);
const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState<string>();
  const [isManualAddSteelAssembly, setIsManualAddSteelAssembly] =
    useState(false);
  const [projectNodeData, setProjectNodeData] = useState<any>(null);
  const [newSteelAssembly, setNewSteelAssembly] = useState({
    steelAssemblyId: "",
    steelAssemblyCode: "",
    //guandaId: "",
    installationLocation: "",
    installationElevation: "",
    distinction: "",
    transportNumber: "",
    transportDescription: "",
    drawingName: "",
    totalWidth: "",
    totalHeight: "",
    totalLength: "",
    totalWeight: "",
    totalArea: "",
    metricSizeMark: "",
  });

  console.log('projectNodeData', projectNodeData)


  const [employees, setEmployees] = useState<any[]>([]);
  const [isCreatingWorkItem, setIsCreatingWorkItem] = useState(false);
  const [tempTitleValue, setTempTitleValue] = useState<string>();
  const [projectAttachments, setProjectAttachments] = useState<any[]>([]);
  const [modalDesignBOMData, setModalDesignBOMData] = useState<any[]>([]);
  const [allFilteredModalData, setAllFilteredModalData] = useState<any[]>([]);
  const [modalTotalItems, setModalTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalFilters, setModalFiltersState] = useState({
    guandaId: "",
    componentCode: "",
    installationLocation: "",
    metricSizeMark: "",
    transportNumber: "",
  });
  const [qrCodePopup, setQrCodePopup] = useState<{
    visible: boolean;
    guandaId: number | null;
    code: string | null;
  }>({ visible: false, guandaId: null, code: null });
  const [subtreeWorkGroups, setSubtreeWorkGroups] = useState<
    { group: string; items: { key: string; label: string }[] }[]
  >([]);
  const [workTaskMap, setWorkTaskMap] = useState<
    Record<string, Record<string, WorkTask>>
  >({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState<any>(null);
  const [historyTask, setHistoryTask] = useState<WorkTask | null>(null);
  const [historyLabel, setHistoryLabel] = useState("");

  // Use selection state from context
  const selectedBOMRow = (selectionState.selectedBOMRows || []).filter(Boolean);
  const modalCurrentPage = selectionState.modalCurrentPage;
  const modalPageSize = selectionState.modalPageSize;
  const filters = selectionState.modalFilters;

  const { id: projectId } = useParams();
  const ifc3d = useProjectIfc3d(projectId, isModelOpen || isSecondModalOpen);

  const documentId = data.documentId;

  const COLUMN_VISIBILITY_STORAGE_KEY = `productionPlanNode_columnVisibility_${projectId ?? "all"}`;

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(
        `productionPlanNode_columnVisibility_${projectId ?? "all"}`
      );
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!isModelOpen) return;
    try {
      const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
      setHiddenColumns(saved ? JSON.parse(saved) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, [isModelOpen, COLUMN_VISIBILITY_STORAGE_KEY]);

  const toggleColumn = (key: string, checked: boolean) => {
    const next = checked
      ? hiddenColumns.filter((k) => k !== key)
      : [...hiddenColumns, key];
    setHiddenColumns(next);
    localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(next));
  };

  const fetchProjectAttachments = async () => {
    if (!projectId) return;
    try {
      const projectData = await single(
        orpcClient.project.read([{ id: projectId }]),
      );
      if (projectData?.attachment) {
        setProjectAttachments(projectData.attachment as any[]);
      }
    } catch (error) {
      console.error("Error fetching project attachments:", error);
    }
  };

  const findMatchingPdf = (drawingName: string) => {
    if (!drawingName || !projectAttachments?.length) return null;
    return projectAttachments.find((file: any) => {
      if (!file?.name || !file?.url) return false;
      const isPdf = file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) return false;
      const fileNameWithoutExt = file.name.replace(/\.pdf$/i, "");
      return fileNameWithoutExt.trim() === drawingName.trim();
    });
  };
  const onChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    console.log(evt.target.value);
  }, []);

  const handleModalPageChange = (page: number, size?: number) => {
    setModalCurrentPage(page);
    if (size && size !== modalPageSize) {
      setModalPageSize(size);
      updateModalPageData(page, size);
    } else {
      updateModalPageData(page, modalPageSize);
    }
  };

  const fetchAllFilteredModalData = async () => {
    setModalLoading(true);
    try {
      let allData: any[] = [];

      if (projectNodeData?.parentNodeId) {
        const parentNode = await single(
          orpcClient.projectNode.read([{ id: projectNodeData.parentNodeId }]),
        );


        if (
          parentNode &&
          parentNode.steelAssembly &&
          parentNode.steelAssembly.length > 0
        ) {
          // const parentDesignBomIds = parentNode.steelAssembly.map(
          //   (item: any) => item?.buildingId || item?.buildingId
          // );

          // console.log('parentNode.steelAssembly', parentNode.steelAssembly)
          // console.log('parentDesignBomIds', parentDesignBomIds)

          // // const parentDesignBoms = await Promise.all(
          // //   parentDesignBomIds.map((id: string) =>
          // //     orpcClient.steelAssembly.list({ buildingId: id})
          // //   )
          // // );
          // //allData = parentDesignBoms.filter(Boolean);
          // allData = await orpcClient.steelAssembly.list({ buildingId: parentDesignBomIds[0]})

          allData = parentNode.steelAssembly
        } else {
          allData = [];
        }
      } else {
        const projectData = await single(
          orpcClient.project.read([{ id: projectId! }]),
        );
        const buildingId = projectData?.projectNode?.filter(
          (t) => t?.nodeType === "building"
        )[0]?.id;

        if (buildingId) {
          allData = await orpcClient.steelAssembly.list({
            filter: { buildingId: buildingId },
          });
        } else {
          allData = await orpcClient.steelAssembly.list({
            filter: { projectId: projectId },
          });
        }
      }

      if (!allData || allData.length === 0) {
        setOpenKeys({});
        setAllComponentOptions([]);
        return;
      }

      let siblingSelectedIdsTemp: string[] = [];
      if (projectNodeData?.parentNodeId) {
        try {
          const allNodes = await orpcClient.projectNode.list({
            filter: { projectId: projectId! },
          });

          const siblingNodes = allNodes.filter(
            (node: any) =>
              node.parentNodeId === projectNodeData.parentNodeId &&
              node.id !== documentId
          );

          const siblingDataResults = siblingNodes.length
            ? await allOrThrow(
                orpcClient.projectNode.read(
                  siblingNodes.map((sibling: any) => ({ id: sibling.id }))
                )
              )
            : [];

          const siblingDesignBomIds = siblingDataResults.flatMap(
            (siblingData: any) =>
              (siblingData.steelAssembly || []).map((item: any) => item?.id)
          );

          siblingSelectedIdsTemp = siblingDesignBomIds.filter(Boolean);
          setSiblingSelectedIds(siblingSelectedIdsTemp);
        } catch (error) {
          console.error("Error fetching sibling nodes:", error);
        }
      }

      const nodeLevelKey =
        projectNodeData?.parentNodeId && projectNodeData?.nodeType
          ? `${projectNodeData.nodeType}Id`
          : null;

      const allFilteredItems = allData
        .filter(
          (item) =>
            !steelAssemblyDetails?.some(
              (existing) => (existing.documentId || existing.id) === item.id
            ) &&
            !siblingSelectedIdsTemp.includes(item.id) &&
            (!nodeLevelKey || !item[nodeLevelKey])
        )
        .map((item) => ({
          ...item,
          documentId: item.id,
        }));

      const filteredItems = allFilteredItems.filter((item) => {
        const guandaId = String(item?.guandaId || "");
        const componentCode = String(item?.steelAssemblyCode || "");
        const distinction = String(item?.distinction || "");
        const metricSizeMark = String(item?.metricSizeMark || "");
        const transportNumber = String(item?.transportNumber || "");

        return (
          guandaId
            .toLowerCase()
            .includes((modalFilters.guandaId || "").toLowerCase()) &&
          componentCode
            .toLowerCase()
            .includes((modalFilters.componentCode || "").toLowerCase()) &&
          distinction
            .toLowerCase()
            .includes((modalFilters.distinction || "").toLowerCase()) &&
          metricSizeMark
            .toLowerCase()
            .includes((modalFilters.metricSizeMark || "").toLowerCase()) &&
          transportNumber
            .toLowerCase()
            .includes((modalFilters.transportNumber || "").toLowerCase())
        );
      });

      // Store all filtered data and set total count
      setAllFilteredModalData(filteredItems);
      setModalTotalItems(filteredItems.length);

      // Set data for current page
      updateModalPageData(modalCurrentPage, modalPageSize, filteredItems);
    } catch (error) {
      console.error("Error fetching modal design BOM data:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const updateModalPageData = (
    page: number,
    size: number,
    data: any[] = allFilteredModalData
  ) => {
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const pageItems = data.slice(startIndex, endIndex);
    setModalDesignBOMData(pageItems);
  };

  const fetchMaterialBomData = async (page = 1) => {
    try {
      const allData = await orpcClient.steelAssembly.list({
        filter: { projectId: projectId },
      });



      setMaterialBomData(allData);
    } catch (error) {
      console.error("Error fetching material BOM data:", error);
      notification.error({
        message: t("textUpdateNode.fetchError") || "載入失敗",
        description:
          t("textUpdateNode.fetchMaterialBomError") ||
          "無法載入構件清單資料，請稍後再試",
      });
    }
  };

  const [siblingSelectedIds, setSiblingSelectedIds] = useState<string[]>([]);

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedBOMRows(selectedRows.filter(Boolean));
    },
    getCheckboxProps: (record: any) => ({
      disabled:
        steelAssemblyDetails?.some(
          (existing) =>
            (existing.documentId || existing.id) ===
            (record.documentId || record.id)
        ) || siblingSelectedIds.includes(record.documentId || record.id),
      name: record.name,
    }),
    selectedRowKeys: selectedBOMRow.map((row: any) => row.documentId),
  };

  // const tempData = [
  //   {
  //     key: '1',
  //     name: 'John Brown',
  //     age: 32,
  //     address: 'New York No. 1 Lake Park',
  //     tags: ['nice', 'developer'],
  //   },
  //   {
  //     key: '2',
  //     name: 'Jim Green',
  //     age: 42,
  //     address: 'London No. 1 Lake Park',
  //     tags: ['loser'],
  //   },
  //   {
  //     key: '3',
  //     name: 'Joe Black',
  //     age: 32,
  //     address: 'Sydney No. 1 Lake Park',
  //     tags: ['cool', 'teacher'],
  //   },
  // ];

  // Base columns without actions (for selection table)
  const baseColumns = [
    {
      title: t("textUpdateNode.componentId") || "構件ID",
      dataIndex: "steelAssemblyId",
      fixed: "left" as const,

      render: (text, i) => {

        return i?.steelAssemblyId?.trim();
        return (
          <Input
            placeholder="Input component"
            disabled={true}
            //leftSection={<IconMail size={16} />}
            value={i?.steelAssemblyId?.trim()}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.guandaId") || "廣達ID",
      dataIndex: "guandaId",
      render: (text, i) => {
        if (!i?.guandaId) return null;
        return (
          <span
            style={{ color: "#1677ff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() =>
              setQrCodePopup({
                visible: true,
                guandaId: i.guandaId,
                code: i.steelAssemblyCode,
              })
            }
          >
            {i?.guandaId?.toString().padStart(7, "0")}
          </span>
        );
      },
    },
    {
      title: t("textUpdateNode.componentCode") || "構件編號",
      dataIndex: "steelAssemblyCode",

      render: (text, i) => {
        return i?.steelAssemblyCode?.trim() || "-";
        return (
          <Input
            placeholder="Input component"
            disabled={true}
            //leftSection={<IconMail size={16} />}
            value={i?.steelAssemblyCode?.trim()}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.installationLocation") || "安裝位置",
      dataIndex: "installationLocation",
      render: (text, i) => {
        return i?.installationLocation;
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.installationLocation}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.installationElevation") || "安裝高程",
      dataIndex: "installationElevation",
      render: (text, i) => {
        return i?.installationElevation;
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.installationElevation}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.distinction") || "區別",
      dataIndex: "distinction",
      render: (text, i) => {
        return i?.distinction || "-";
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            value={i?.distinction}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.transportNumber") || "拆運號碼",
      dataIndex: "transportNumber",
      render: (text, i) => {
        return i?.transportNumber || "-";
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.transportNumber}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.transportDescription") || "拆運說明",
      dataIndex: "transportDescription",
      render: (text, i) => {
        return i?.transportDescription;
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.transportDescription}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.drawingName") || "圖面名稱",
      dataIndex: "drawingName",
      render: (text, i) => {
        if (!i?.drawingName)
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        const matchingPdf = findMatchingPdf(i?.drawingName);
        if (matchingPdf) {
          return (
            <a
              href={matchingPdf.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1890ff", textDecoration: "underline" }}
            >
              {i?.drawingName}
            </a>
          );
        }
        return i?.drawingName;
      },
    },
    ...(ifc3d.enabled
      ? [
          {
            title: ifc3d.title,
            dataIndex: "model3d",
            render: (_: unknown, i: any) => ifc3d.render3dCell(assemblyMarks(i)),
          },
        ]
      : []),
    {
      title: t("textUpdateNode.totalWidth") || "總寬度",
      dataIndex: "totalWidth",
      render: (text, i) => {
        return i?.totalWidth;
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.totalWidth}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.totalHeight") || "總高度",
      dataIndex: "totalHeight",
      render: (text, i) => {
        return i?.totalHeight;
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.totalHeight}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.totalLength") || "總長度",
      dataIndex: "totalLength",
      render: (text, i) => {
        return i?.totalLength;
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.totalLength}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.totalWeightSingle") || "總重量(單支)",
      dataIndex: "totalWeight",
      render: (text, i) => {
        return i?.totalWeight != null && i?.totalWeight !== ""
          ? `${i?.totalWeight} kg`
          : "-";
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.totalWeight}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.totalArea") || "總面積",
      dataIndex: "totalArea",
      render: (text, i) => {
        return i?.totalArea;
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={i?.totalArea}
          />
        );
      },
    },
    {
      title: t("textUpdateNode.metricSizeMark") || "斷面規格",
      dataIndex: "metricSizeMark",
      render: (text, i) => {
        return i?.metricSizeMark || "-";
        return (
          <Input
            disabled={true}
            placeholder="Input component"
            value={i?.metricSizeMark}
          />
        );
      },
    },
    // {
    //   title: '',
    //   fixed: 'right',
    //   render: (text, i) => {
    //     return (
    //       <>
    //         {editRowId ? (
    //           <Button
    //             variant="default"
    //             style={{ marginRight: '15px' }}
    //             onClick={() => {
    //               setEditRowId(null);
    //               setEditRowData([]);
    //             }}
    //           >
    //             取消
    //           </Button>
    //         ) : (
    //           <Button
    //             variant="default"
    //             style={{ marginRight: '15px' }}
    //             onClick={() => {
    //               setEditRowId(i?.id);
    //               setEditRowData(i);
    //             }}
    //           >
    //             編輯
    //           </Button>
    //         )}
    //         <Button
    //           variant="default"
    //           onClick={async (element) => {
    //             const result = await apiUserPutMaterialBOM({
    //               id: i?.documentId,
    //               data: {
    //                 steelAssembly: {
    //                   steelAssemblyId: String(i?.steelAssemblyId),
    //                   steelAssemblyCode: i?.steelAssemblyCode,
    //                   installationLocation: i?.installationLocation,
    //                   installationElevation: Number(
    //                     i?.installationElevation,
    //                   ),
    //                   distinction: String(i?.distinction),
    //                   transportNumber: i?.transportNumber,
    //                   transportDescription: i?.transportDescription,
    //                   drawingName: i?.drawingName,
    //                   totalWidth: i?.totalWidth,
    //                   totalHeight: i?.totalHeight,
    //                   totalLength: i?.totalLength,
    //                   totalWeight: i?.totalWeight,
    //                   totalArea: i?.totalArea,
    //                 },
    //               },
    //             });

    //             if (result?.data) {
    //               api.success({
    //                 message: `Notification `,
    //                 description: <div>編輯成功</div>,
    //                 placement: 'topLeft',
    //                 duration: 0,
    //               });
    //             } else {
    //               api.error({
    //                 message: `Notification `,
    //                 description: <div>編輯失敗</div>,
    //                 placement: 'topLeft',
    //                 duration: 0,
    //               });
    //             }

    //             fetchData();
    //             setEditRowId(null);
    //             setEditRowData([]);
    //           }}
    //         >
    //           儲存
    //         </Button>
    //       </>
    //     );
    //   },
  ];

  const bomColumnWidths: Record<string, number> = {
    steelAssemblyId: 120,
    guandaId: 100,
    steelAssemblyCode: 110,
    installationLocation: 120,
    installationElevation: 100,
    distinction: 80,
    transportNumber: 110,
    transportDescription: 150,
    drawingName: 150,
    model3d: 100,
    totalWidth: 100,
    totalHeight: 100,
    totalLength: 100,
    totalWeight: 120,
    totalArea: 100,
    metricSizeMark: 150,
  };

  const planBomColumnKeys = [
    "guandaId",
    "steelAssemblyCode",
    "totalWeight",
    "distinction",
    "transportNumber",
    "drawingName",
    ...(ifc3d.enabled ? ["model3d"] : []),
    "metricSizeMark",
  ];

  const bomColumnOptions = [
    { key: "guandaId", label: t("textUpdateNode.guandaId") || "廣達ID" },
    {
      key: "steelAssemblyCode",
      label: t("textUpdateNode.componentCode") || "構件編號",
    },
    {
      key: "totalWeight",
      label: t("textUpdateNode.totalWeightSingle") || "總重量(單支)",
    },
    { key: "distinction", label: t("textUpdateNode.distinction") || "區別" },
    {
      key: "transportNumber",
      label: t("textUpdateNode.transportNumber") || "拆運號碼",
    },
    { key: "drawingName", label: t("textUpdateNode.drawingName") || "圖面名稱" },
    ...(ifc3d.enabled ? [{ key: "model3d", label: ifc3d.title }] : []),
    {
      key: "metricSizeMark",
      label: t("textUpdateNode.metricSizeMark") || "斷面規格",
    },
  ];

  const workColumnOptions = [
    { key: "worker", label: t("textUpdateNode.workerName") || "施工人員" },
    { key: "status", label: t("textUpdateNode.status") || "狀態" },
    { key: "reportDate", label: t("textUpdateNode.reportDate") || "回報日期" },
  ];

  const openWorkHistory = (
    record: any,
    item: { key: string; label: string }
  ) => {
    setHistoryRow({
      componentNo: record?.steelAssemblyCode || "-",
      qdId: record?.guandaId
        ? String(record.guandaId).padStart(7, "0")
        : record?.steelAssemblyId || "-",
    });
    setHistoryTask(workTaskMap[record?.id]?.[item.key] ?? null);
    setHistoryLabel(item.label);
    setHistoryOpen(true);
  };

  const workStatusTag = (status: ReportStatus | null) => {
    if (status === "success")
      return (
        <Tag icon={<CheckCircleFilled />} color="success">
          {t("textUpdateNode.statusCompleted") || "完成"}
        </Tag>
      );
    if (status === "error")
      return (
        <Tag icon={<CloseCircleFilled />} color="error">
          {t("textUpdateNode.statusError") || "錯誤"}
        </Tag>
      );
    return (
      <Tag icon={<MinusCircleOutlined />} color="default">
        {t("textUpdateNode.statusNotReported") || "未回報"}
      </Tag>
    );
  };

  const buildWorkChildren = (item: { key: string; label: string }) => [
    {
      title: t("textUpdateNode.workerName") || "施工人員",
      columnKey: "worker",
      key: `${item.key}-worker`,
      width: 150,
      render: (_: unknown, record: any) => {
        const task = workTaskMap[record?.id]?.[item.key];
        if (!task?.worker) return <span style={{ color: "#bfbfbf" }}>—</span>;
        return (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Avatar size={32} src={task.workerPhoto}>
              {task.worker.slice(0, 1)}
            </Avatar>
            {task.worker}
          </span>
        );
      },
    },
    {
      title: t("textUpdateNode.status") || "狀態",
      columnKey: "status",
      key: `${item.key}-status`,
      width: 100,
      align: "center" as const,
      render: (_: unknown, record: any) =>
        workStatusTag(workTaskMap[record?.id]?.[item.key]?.status ?? null),
    },
    {
      title: t("textUpdateNode.reportDate") || "回報日期",
      columnKey: "reportDate",
      key: `${item.key}-date`,
      width: 160,
      render: (_: unknown, record: any) => {
        const task = workTaskMap[record?.id]?.[item.key];
        if (!task || task.history.length === 0)
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        return (
          <Typography.Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              openWorkHistory(record, item);
            }}
          >
            {task.reportDate ||
              t("textUpdateNode.viewRecords") ||
              "查看紀錄"}
          </Typography.Link>
        );
      },
    },
  ]
    .filter((col) => !hiddenColumns.includes(col.columnKey))
    .map(({ columnKey, ...col }) => col);

  const planBomChildren = planBomColumnKeys
    .filter((key) => !hiddenColumns.includes(key))
    .map((key) => baseColumns.find((col) => col.dataIndex === key))
    .filter(Boolean)
    .map((col: any) => ({
      ...col,
      fixed: undefined,
      width: bomColumnWidths[col.dataIndex] ?? 120,
    }));

  const columns = [
    ...(planBomChildren.length
      ? [
          {
            title: t("textUpdateNode.planBom") || "生產計劃BOM",
            key: "planBom",
            className: "group-bom",
            children: planBomChildren,
          },
        ]
      : []),
    ...subtreeWorkGroups
      .map((group, groupIndex) => ({
        title: group.group,
        key: `work-group-${groupIndex}`,
        className: "group-zone",
        children: group.items
          .map((item) => ({
            title: item.label,
            key: `work-item-${item.key}`,
            className: "group-work",
            children: buildWorkChildren(item),
          }))
          .filter((item) => item.children.length > 0),
      }))
      .filter((group) => group.children.length > 0),
    {
      title: t("textUpdateNode.actions") || "操作",
      fixed: "right" as const,
      width: 100,
      render: (text, record) => {
        return (
          <Button
            variant="default"
            style={{ backgroundColor: "#ff4d4f", color: "white" }}
            onClick={() => handleRemoveSteelAssembly(record)}
          >
            {t("textUpdateNode.remove") || "移除"}
          </Button>
        );
      },
    },
  ];

  const handleEditClick = () => {
    setIsModalOpen(true);
  };
  const handleSubmit = async (formValue: WorkItemDialogValue) => {
    try {
      if (!documentId) {
        console.error("No documentId available for this node");
        notification.error({
          message: t("textUpdateNode.error") || "錯誤",
          description:
            t("textUpdateNode.noDocumentId") || "找不到節點ID，無法建立工項",
        });
        return;
      }

      if (!formValue.name.trim()) {
        notification.warning({
          message: t("textUpdateNode.warning") || "警告",
          description:
            t("textUpdateNode.workItemNameRequired") || "請輸入工項名稱",
        });
        return;
      }

      if (formValue.employees.length === 0) {
        notification.warning({
          message: t("textUpdateNode.warning") || "警告",
          description:
            t("textUpdateNode.employeeRequired") || "請至少選擇一名員工",
        });
        return;
      }

      setIsCreatingWorkItem(true);
      const existingWorkItems = projectNodeData?.workItem || [];
      if (existingWorkItems.length > 0) {
        await allOrThrow(
          orpcClient.workItem.update(
            existingWorkItems.map((wi: any) => ({
              id: wi.id,
              order: (wi.order ?? 0) + 1,
            }))
          )
        );
      }

      const result = await single(
        orpcClient.workItem.create([
          {
            projectNodeId: documentId!,
            name: formValue.name,
            scanMode: formValue.scanMode,
            reportPresetTexts:
              formValue.reportTexts.length > 0
                ? { texts: formValue.reportTexts }
                : null,
            order: 0,
          },
        ])
      );

      if (result) {
        await orpcClient.workItem.addEmployee({
          workItemId: result.id,
          employeeId: formValue.employees,
        });

        notification.success({
          message: t("textUpdateNode.success") || "成功",
          description:
            t("textUpdateNode.workItemCreatedSuccess") || "工項已成功建立",
        });

        setIsSecondModalOpen(false);
        fetchNodeData();
      } else {
        notification.error({
          message: t("textUpdateNode.createFailed") || "建立失敗",
          description:
            t("textUpdateNode.createWorkItemError") ||
            "無法建立工項，請稍後再試",
        });
      }
    } catch (error) {
      console.error("Error creating work item:", error);
      notification.error({
        message: t("textUpdateNode.createFailed") || "建立失敗",
        description:
          error instanceof Error
            ? error.message
            : t("textUpdateNode.workItemCreateError") ||
              "無法建立工項，請稍後再試",
      });
    } finally {
      setIsCreatingWorkItem(false);
    }
  };

  const fetchBOMsByDocumentIds = async (
    documentIds: string[]
  ): Promise<any[]> => {
    try {
      if (documentIds.length === 0) return [];
      const bomDetails = await allOrThrow(
        orpcClient.steelAssembly.read(documentIds.map((id) => ({ id })))
      );
      return bomDetails.filter(Boolean);
    } catch (error) {
      console.error("Error fetching BOMs by document IDs:", error);
      return [];
    }
  };

  const fetchNodeData = async () => {
    if (documentId) {
      try {
        const result = await single(
          orpcClient.projectNode.read([{ id: documentId }]),
        );

        if (result) {
          const workItemsWithEmployees = await orpcClient.workItem.list({
            filter: { projectNodeId: documentId! },
          });

          const enrichedResult = {
            ...result,
            workItem: (result.workItem || []).map((wi: any) => {
              const wiWithEmp = workItemsWithEmployees.find((w: any) => w.id === wi.id);
              return { ...wi, employee: wiWithEmp?.employee || [] };
            }),
          };

          setProjectNodeData(enrichedResult);
          setTitleValue(result.name ?? undefined);

          setSteelAssemblyList(result.steelAssembly || []);
          setSteelAssemblyStats(result?.steelAssemblyStats ?? {});
          setSteelAssemblyDetails(result.steelAssembly);
        } else {
          notification.error({
            message: t("textUpdateNode.fetchError") || "載入失敗",
            description: t("textUpdateNode.noNodeData") || "無法取得節點資料",
          });
        }
      } catch (error) {
        console.error("Error fetching node data:", error);
        notification.error({
          message: t("textUpdateNode.fetchError") || "載入失敗",
          description:
            error instanceof Error
              ? error.message
              : t("textUpdateNode.fetchNodeDataError") ||
                "無法載入節點資料，請稍後再試",
        });
      }
    }
  };

  const fetchSubtreeWorkProgress = async () => {
    if (!projectId || !documentId) return;

    try {
      const allNodes = (await orpcClient.projectNode.list({
        filter: { projectId },
      })) as any[];

      const childrenByParent = new Map<string, any[]>();
      allNodes.forEach((node: any) => {
        const parentId = node.parentNodeId || "";
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
        childrenByParent.get(parentId)!.push(node);
      });

      const selfNode = allNodes.find((node: any) => node.id === documentId);
      if (!selfNode) {
        setSubtreeWorkGroups([]);
        setWorkTaskMap({});
        return;
      }

      const subtreeNodes: any[] = [];
      const collect = (node: any) => {
        subtreeNodes.push(node);
        (childrenByParent.get(node.id) || []).forEach(collect);
      };
      collect(selfNode);

      const nodeWorkItems = await Promise.all(
        subtreeNodes.map(async (node: any) => {
          const workItems = (await orpcClient.workItem.list({
            filter: { projectNodeId: node.id },
          })) as any[];
          return {
            node,
            workItems: [...workItems].sort(
              (a, b) => (a.order ?? 0) - (b.order ?? 0)
            ),
          };
        })
      );

      const activeNodeWorkItems = nodeWorkItems.filter(
        (n) => n.workItems.length > 0
      );
      const allWorkItems = activeNodeWorkItems.flatMap((n) => n.workItems);

      const scanHistoryByWorkItem = await Promise.all(
        allWorkItems.map(async (wi: any) => ({
          workItemId: wi.id,
          history: (await orpcClient.scanHistory.list({
            filter: { workItemId: wi.id },
          })) as any[],
        }))
      );

      const recordsMap: Record<string, Record<string, HistoryRecord[]>> = {};

      scanHistoryByWorkItem.forEach(({ workItemId, history }) => {
        history.forEach((rec: any) => {
          const record: HistoryRecord = {
            id: rec.id,
            status: rec.scanStatus as ReportStatus,
            description: rec.description || "",
            personName: rec.employee?.name || "N/A",
            personPhoto: rec.employee?.profile || undefined,
            photo: rec.photoUrl || undefined,
            reportedAt: rec.reportTime
              ? dayjs(rec.reportTime).format("YYYY-MM-DD HH:mm")
              : "",
            revoked: Boolean(rec.revokedAt),
          };

          (rec.steelAssembly ?? []).forEach((link: any) => {
            const saId = link.steelAssemblyId;
            if (!saId) return;
            if (!recordsMap[saId]) recordsMap[saId] = {};
            if (!recordsMap[saId][workItemId]) recordsMap[saId][workItemId] = [];
            recordsMap[saId][workItemId].push(record);
          });
        });
      });

      const taskMap: Record<string, Record<string, WorkTask>> = {};
      Object.keys(recordsMap).forEach((saId) => {
        taskMap[saId] = {};
        Object.keys(recordsMap[saId]).forEach((workItemId) => {
          const history = recordsMap[saId][workItemId]
            .slice()
            .sort(
              (a, b) =>
                dayjs(b.reportedAt).valueOf() - dayjs(a.reportedAt).valueOf()
            );
          const latestValid = history.find((h) => !h.revoked);
          taskMap[saId][workItemId] = {
            worker: latestValid?.personName ?? "",
            workerPhoto: latestValid?.personPhoto,
            status: latestValid ? latestValid.status : null,
            reportDate: latestValid?.reportedAt ?? "",
            history,
          };
        });
      });

      setSubtreeWorkGroups(
        activeNodeWorkItems.map(({ node, workItems }) => ({
          group:
            node.name || NODE_TYPE_LABEL[node.nodeType] || node.nodeType || "",
          items: workItems.map((wi: any) => ({ key: wi.id, label: wi.name })),
        }))
      );
      setWorkTaskMap(taskMap);
    } catch (error) {
      console.error("Error loading subtree work progress:", error);
      setSubtreeWorkGroups([]);
      setWorkTaskMap({});
    }
  };

  const handleSubmitAddSteelAssembly = async () => {
    try {
      if (!documentId) {
        console.error("No documentId available for this node");
        notification.error({
          message: t("textUpdateNode.error") || "錯誤",
          description:
            t("textUpdateNode.noDocumentId") ||
            "找不到節點ID，無法更新構件清單",
        });
        return;
      }

      if (selectedBOMRow.length === 0) {
        notification.warning({
          message: t("textUpdateNode.warning") || "警告",
          description:
            t("textUpdateNode.noSelectedComponents") || "請至少選擇一個構件",
        });
        return;
      }

      const selectedDocumentIds = selectedBOMRow.map(
        (row: any) => row.documentId
      );

      const existingIds = steelAssemblyDetails.map(
        (item: any) => item.documentId
      );
      const duplicateIds = selectedDocumentIds.filter((id) =>
        existingIds.includes(id)
      );

      if (duplicateIds.length > 0) {
        notification.warning({
          message: t("textUpdateNode.warning") || "警告",
          description: "構件包中的選取構件只能新增一次，請選擇其他構件",
        });
        return;
      }

      if (projectNodeData?.parentNodeId) {
        const allNodes = await orpcClient.projectNode.list({
          filter: { projectId: projectId! },
        });

        const siblingNodes = allNodes.filter(
          (node: any) =>
            node.parentNodeId === projectNodeData.parentNodeId &&
            node.id !== documentId
        );

        const siblingDataResults = siblingNodes.length
          ? await allOrThrow(
              orpcClient.projectNode.read(
                siblingNodes.map((sibling: any) => ({ id: sibling.id }))
              )
            )
          : [];

        const siblingDesignBomIds = siblingDataResults.flatMap(
          (siblingData: any) =>
            (siblingData.steelAssembly || []).map((item: any) => item?.id)
        );

        const siblingSelectedIds = siblingDesignBomIds.filter(Boolean);

        const alreadySelectedBySiblings = selectedDocumentIds.filter((id) =>
          siblingSelectedIds.includes(id)
        );

        if (alreadySelectedBySiblings.length > 0) {
          notification.warning({
            message: t("textUpdateNode.warning") || "警告",
            description: "所選構件已被同層級的其他節點選取，無法重複選擇",
          });
          return;
        }
      }

      await orpcClient.projectNode.addSteelAssembly({
        projectNodeId: documentId,
        steelAssemblyId: selectedDocumentIds,
        //parentNode: projectNodeData?.id,
      });

      notification.success({
        message: t("textUpdateNode.success") || "成功",
        description:
          t("textUpdateNode.componentsAddedSuccess") || "構件已成功新增",
      });

      fetchNodeData();
      setIsAddSteelAssembly(false);
      setSelectedBOMRows([]);
    } catch (error) {
      console.error("Error updating steel assembly list:", error);
      notification.error({
        message: t("textUpdateNode.updateFailed") || "更新失敗",
        description:
          error instanceof Error
            ? error.message
            : t("textUpdateNode.updateSteelAssemblyError") ||
              "無法更新構件清單，請稍後再試",
      });
    }
  };

  const handleRemoveSteelAssembly = async (record: any) => {
    try {
      if (!documentId) {
        notification.error({
          message: t("textUpdateNode.error") || "錯誤",
          description:
            t("textUpdateNode.noDocumentId") || "找不到節點ID，無法移除構件",
        });
        return;
      }

      const steelAssemblyToRemove = [record.id];

      await orpcClient.projectNode.removeSteelAssembly({
        projectNodeId: documentId,
        steelAssemblyId: steelAssemblyToRemove,
      });

      notification.success({
        message: t("textUpdateNode.success") || "成功",
        description:
          t("textUpdateNode.componentRemovedSuccess") || "構件已成功移除",
      });

      fetchNodeData();
    } catch (error) {
      console.error("Error removing steel assembly:", error);
      notification.error({
        message: t("textUpdateNode.removeFailed") || "移除失敗",
        description:
          error instanceof Error
            ? error.message
            : t("textUpdateNode.removeSteelAssemblyError") ||
              "無法移除構件，請稍後再試",
      });
    }
  };
  const resolveBuildingId = async (): Promise<string | null> => {
    if (projectNodeData?.nodeType === "building") return documentId ?? null;
    if (!projectId) return null;
    const projectData = await single(
      orpcClient.project.read([{ id: projectId }]),
    );
    return (
      projectData?.projectNode?.find((t: any) => t?.nodeType === "building")
        ?.id ?? null
    );
  };

  const handleManualAddSteelAssembly = async () => {
    try {
      if (!projectId) {
        notification.error({
          message: t("textUpdateNode.error") || "錯誤",
          description:
            t("textUpdateNode.noProjectId") || "找不到專案ID，無法新增構件",
        });
        return;
      }

      if (!newSteelAssembly.steelAssemblyId.trim()) {
        notification.warning({
          message: t("textUpdateNode.warning") || "警告",
          description:
            t("textUpdateNode.componentIdRequired") || "構件ID是必填欄位",
        });
        return;
      }

      if (!newSteelAssembly.steelAssemblyCode.trim()) {
        notification.warning({
          message: t("textUpdateNode.warning") || "警告",
          description:
            t("textUpdateNode.componentCodeRequired") || "構件編號是必填欄位",
        });
        return;
      }

      const buildingId = await resolveBuildingId();
      if (!buildingId) {
        notification.error({
          message: t("textUpdateNode.error") || "錯誤",
          description:
            t("textUpdateNode.noBuildingNode") ||
            "找不到整體建築節點，無法新增構件",
        });
        return;
      }

      const steelAssemblyResult = await single(
        orpcClient.steelAssembly.create([
          {
            buildingId,
            steelAssemblyId: String(newSteelAssembly.steelAssemblyId),
            steelAssemblyCode: newSteelAssembly.steelAssemblyCode,
            installationLocation: newSteelAssembly.installationLocation,
            installationElevation: newSteelAssembly.installationElevation
              ? parseFloat(newSteelAssembly.installationElevation)
              : null,
            distinction: String(newSteelAssembly.distinction),
            transportNumber: newSteelAssembly.transportNumber,
            transportDescription: newSteelAssembly.transportDescription,
            drawingName: newSteelAssembly.drawingName,
            totalWidth: newSteelAssembly.totalWidth
              ? parseFloat(newSteelAssembly.totalWidth)
              : null,
            totalHeight: newSteelAssembly.totalHeight
              ? parseFloat(newSteelAssembly.totalHeight)
              : null,
            totalLength: newSteelAssembly.totalLength
              ? parseFloat(newSteelAssembly.totalLength)
              : null,
            totalWeight: newSteelAssembly.totalWeight
              ? parseFloat(newSteelAssembly.totalWeight)
              : null,
            totalArea: newSteelAssembly.totalArea
              ? parseFloat(newSteelAssembly.totalArea)
              : null,
            metricSizeMark: newSteelAssembly.metricSizeMark,
          },
        ]),
      );

      // 舊流程會再用第一筆的資料庫 id 當 steelAssemblyId 建第二筆構件，
      // 這是殘留的重複建立，改為只建立一筆（與 DesignBOM 的手動新增一致）
      // const result = await single(
      //   orpcClient.steelAssembly.create([
      //     { buildingId, steelAssemblyId: steelAssemblyResult.id },
      //   ]),
      // );

      const result = steelAssemblyResult;

      if (result) {
        // building 節點沒有 parentNode，addSteelAssembly 會拋錯；
        // 這層的歸屬在 create 時就已由 buildingId 決定，不需要再掛一次
        if (projectNodeData?.nodeType !== "building") {
          await orpcClient.projectNode.addSteelAssembly({
            projectNodeId: documentId!,
            steelAssemblyId: [result.id],
          });
        }

        // if (designBOM) {
        //   designBOM.push(designBOMResult.data);
        // }

        notification.success({
          message: t("textUpdateNode.success") || "成功",
          description:
            t("textUpdateNode.componentAddedSuccess") || "構件已成功新增",
        });

        setIsManualAddSteelAssembly(false);
        setNewSteelAssembly({
          steelAssemblyId: "",
          steelAssemblyCode: "",
          //guandaId: "",
          installationLocation: "",
          installationElevation: "",
          distinction: "",
          transportNumber: "",
          transportDescription: "",
          drawingName: "",
          totalWidth: "",
          totalHeight: "",
          totalLength: "",
          totalWeight: "",
          totalArea: "",
          metricSizeMark: "",
        });
        fetchNodeData();
      } else {
        notification.error({
          message: t("textUpdateNode.createFailed") || "建立失敗",
          description:
            t("textUpdateNode.createComponentError") ||
            "無法建立新構件，請稍後再試",
        });
      }
    } catch (error) {
      console.error("Error adding steel assembly:", error);
      notification.error({
        message: t("textUpdateNode.createFailed") || "建立失敗",
        description:
          error instanceof Error
            ? error.message
            : t("textUpdateNode.addComponentError") ||
              "無法新增構件，請稍後再試",
      });
    }
  };

  const fetchEmployees = async () => {
    try {
      const result = await orpcClient.employee.list({});
      const filtered = (result ?? []).filter((emp) => emp.isProjectReport === true);
      if (filtered.length > 0) {
        setEmployees(filtered);
      } else {
        setEmployees([]);
        notification.warning({
          message: t("textUpdateNode.warning") || "警告",
          description: t("textUpdateNode.noEmployeeData") || "無員工資料",
        });
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      notification.error({
        message: t("textUpdateNode.fetchError") || "載入失敗",
        description:
          error instanceof Error
            ? error.message
            : t("textUpdateNode.fetchEmployeeError") ||
              "無法載入員工資料，請稍後再試",
      });
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchProjectAttachments();
  }, []);

  // useEffect(() => {
  //   if (isModelOpen) {
  //     fetchMaterialBomData();
  //   }
  // }, [isModelOpen]);

  useEffect(() => {
    fetchNodeData();
    if (isModelOpen) {
      fetchSubtreeWorkProgress();
    }
  }, [isModelOpen]);

  useEffect(() => {
    const loadDataForModal = async () => {
      if (isAddSteelAssembly) {
        await fetchNodeData();
        setModalCurrentPage(1);
        fetchAllFilteredModalData();
      }
    };
    loadDataForModal();
  }, [isAddSteelAssembly]);

  useEffect(() => {
    if (isAddSteelAssembly) {
      fetchAllFilteredModalData();
    }
  }, [modalFilters]);

  const filteredSteelAssemblyDetails =
    steelAssemblyDetails?.filter((item) => {
      const guandaId = String(item?.guandaId || "");
      const componentCode = String(item?.steelAssemblyCode || "");
      const distinction = String(item?.distinction || "");
      const metricSizeMark = String(item?.metricSizeMark || "");
      const transportNumber = String(item?.transportNumber || "");

      return (
        guandaId
          .toLowerCase()
          .includes((filters.guandaId || "").toLowerCase()) &&
        componentCode
          .toLowerCase()
          .includes((filters.componentCode || "").toLowerCase()) &&
        distinction
          .toLowerCase()
          .includes((filters.distinction || "").toLowerCase()) &&
        metricSizeMark
          .toLowerCase()
          .includes((filters.metricSizeMark || "").toLowerCase()) &&
        transportNumber
          .toLowerCase()
          .includes((filters.transportNumber || "").toLowerCase())
      );
    }) || [];

  // Filter out items from designBOM that are already in steelAssemblyDetails
  // const filteredDesignBOM =
  //   designBOM?.filter(
  //     (item) =>
  //       !steelAssemblyDetails?.some(
  //         (existing) =>
  //           existing.steelAssemblyId ===
  //           item.steelAssemblyId
  //       )
  //   ) || [];

  return (
    <>
      <div
        className="min-h-[50px] p-1.5 rounded-md bg-white relative"
        style={{
          border: `${selected ? "4px" : "2px"} solid ${selected ? "#ef4444" : borderColor}`,
        }}
      >
        {data?.priority !== undefined && (
          <div
            className="absolute top-1 left-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{ zIndex: 10 }}
          >
            {data.priority}
          </div>
        )}
        <button
          onClick={handleEditClick}
          className="absolute top-1 right-1 w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center shadow-md hover:bg-gray-300"
        >
          <IconPencil className="w-3 h-3 text-gray-600" />
        </button>

        <div style={{ minWidth: "150px", textAlign: "center" }}>
          {data?.label}
        </div>
        {(steelAssemblyStats?.pending > 0 || steelAssemblyStats?.inProgress > 0 || steelAssemblyStats?.completed > 0) && (
          <div style={{ minWidth: "150px", fontSize: "9px", marginTop: "3px" }} className="nodrag">
            <div className="flex justify-between">
              <span>{t("textUpdateNode.completedComponents") || "已完成加工構件"}</span>
              <span>{steelAssemblyStats?.completed || 0}/{(steelAssemblyStats?.pending || 0) + (steelAssemblyStats?.inProgress || 0) + (steelAssemblyStats?.completed || 0)}</span>
            </div>
            <Progress
              percent={
                (steelAssemblyStats?.pending || 0) + (steelAssemblyStats?.inProgress || 0) + (steelAssemblyStats?.completed || 0) > 0
                  ? Math.round(((steelAssemblyStats?.completed || 0) / ((steelAssemblyStats?.pending || 0) + (steelAssemblyStats?.inProgress || 0) + (steelAssemblyStats?.completed || 0))) * 100)
                  : 0
              }
              size="small"
              status="success"
            />
          </div>
        )}
        {steelAssemblyList.length > 0 && showQRCode && (
          <div className="flex flex-col items-center mt-1">
            <QRCode
              value={String(steelAssemblyList[0]?.guandaId || "0").padStart(7, "0")}
              size={80}
            />
            <div style={{ fontSize: "11px", marginTop: "2px" }}>
              {steelAssemblyList[0]?.steelAssemblyCode}
            </div>
          </div>
        )}
        {type !== "building" && (
          <Handle
            type="target"
            position={Position.Top}
            id="target"
            style={{ left: "50%", transform: "translateX(-50%)" }}
            isConnectable={isConnectable}
          />
        )}
        {type !== "package" && (
          <Handle
            type="source"
            position={Position.Bottom}
            id="a"
            style={{ left: "50%", transform: "translateX(-50%)" }}
            isConnectable={isConnectable}
          />
        )}
      </div>
      <Modal
        width="90%"
        style={{ top: 20 }}
        title={modalContent?.title}
        open={isModelOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setModalFilters({
            guandaId: "",
            componentCode: "",
            distinction: "",
            metricSizeMark: "",
            transportNumber: "",
          });
          setCurrentPage(1);
        }}
        onOk={() => {
          setIsModalOpen(false);
          setModalFilters({
            guandaId: "",
            componentCode: "",
            distinction: "",
            metricSizeMark: "",
            transportNumber: "",
          });
          setCurrentPage(1);
        }}
      >
        <div>
          <div className="flex">
            {isEditingTitle ? (
              <Input
                value={tempTitleValue}
                onChange={(e) => {
                  setTempTitleValue(e.target.value);
                }}
                onBlur={() => {
                  if (documentId && tempTitleValue !== titleValue) {
                    single(
                      orpcClient.projectNode.update([
                        {
                          id: documentId,
                          name: tempTitleValue,
                        },
                      ]),
                    )
                      .then(() => {
                        setTitleValue(tempTitleValue);
                        notification.success({
                          message:
                            t("textUpdateNode.updateSuccess") || "更新成功",
                          description:
                            t("textUpdateNode.nodeNameUpdated") ||
                            "節點名稱已更新",
                        });
                        // Call the callback to update parent component
                        onNodeUpdate?.(documentId, tempTitleValue!);
                      })
                      .catch((error) => {
                        notification.error({
                          message:
                            t("textUpdateNode.updateFailed") || "更新失敗",
                          description:
                            error.message ||
                            t("textUpdateNode.cannotUpdateNodeName") ||
                            "無法更新節點名稱",
                        });
                        setTempTitleValue(titleValue); // Reset to original value on error
                      });
                  }
                  setIsEditingTitle(false);
                }}
                autoFocus
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: "#6F6F6F",
                }}
              />
            ) : (
              <div
                className="py-3 text-2xl font-bold"
                style={{ color: "#6F6F6F" }}
              >
                {titleValue || data.label}
              </div>
            )}
            <button
              onClick={() => {
                setIsEditingTitle(true);
                setTempTitleValue(titleValue || data.label);
              }}
              className="ml-2 p-1 hover:bg-gray-100 rounded-full"
            >
              <IconPencil className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <div className="py-3 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("textUpdateNode.workItemSettings") || "工項設定"}
          </div>
          <div className="flex w-full">
            <div className="basis-3/10  p-4">
              <div
                className="p-2 flex border border-black w-[150px] mt-[14px] items-center text-center min-h-[50px] cursor-pointer hover:bg-gray-200 transition"
                onClick={() => setIsSecondModalOpen(true)}
              >
                <div className="pr-2">
                  <IconPlus className="w-6 h-6 text-gray-600" />
                </div>
                <div className="font-bold">
                  {t("textUpdateNode.addWorkItem") || "增加工項"}
                </div>
              </div>
              <div className="p-2 border-[1px] border-black  w-[150px] mt-[14px] min-h-[50px]">
                <div>
                  {t("textUpdateNode.completedComponents") || "已完成加工構件"}
                </div>
                <div>
                  {steelAssemblyStats?.completed || 0}/{" "}
                  {(steelAssemblyStats?.pending || 0) +
                    (steelAssemblyStats?.inProgress || 0) +
                    (steelAssemblyStats?.completed || 0)}
                </div>
                <Progress
                  percent={
                    (steelAssemblyStats?.pending || 0) +
                      (steelAssemblyStats?.inProgress || 0) +
                      (steelAssemblyStats?.completed || 0) >
                    0
                      ? Math.round(
                          ((steelAssemblyStats?.completed || 0) /
                            ((steelAssemblyStats?.pending || 0) +
                              (steelAssemblyStats?.inProgress || 0) +
                              (steelAssemblyStats?.completed || 0))) *
                            100
                        )
                      : 0
                  }
                  size="small"
                  status="success"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="basis-7/10 p-4 flex-1 overflow-y-auto max-h-[400px]">
              <DragDropList
                workItems={(projectNodeData?.workItem || []).map((wi: any) => ({
                  ...wi,
                  employees: (wi.employee || []).map((emp: any) => {
                    const fullEmp = employees.find((e) => e.id === emp.employeeId);
                    return fullEmp ?? { id: emp.employeeId, name: emp.employeeId, documentId: emp.employeeId };
                  }),
                }))}
                employees={employees}
                fetchNodeData={fetchNodeData}
                totalSteelAssemblies={steelAssemblyDetails?.length || 0}
                projectId={projectId!}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="py-3 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("textUpdateNode.componentList") || "構件清單"}
          </div>
          <div>
            <Button
              onClick={() => setIsAddSteelAssembly(true)}
              variant="default"
              style={{ marginRight: "15px" }}
            >
              {t("textUpdateNode.selectComponents") || "選取構件"}
            </Button>
          </div>
          {/* <div className="ml-3" onClick={() => handleUploadExcel(fileUpload)}>
            <Button variant="default">儲存Excel</Button>
          </div> */}
        </div>
        <div className="mb-4 grid grid-cols-5 gap-4">
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.guandaId") || "廣達ID"}
            </div>
            <Input
              placeholder="搜尋廣達ID"
              value={filters.guandaId}
              onChange={(e) => {
                setModalFilters({ ...filters, guandaId: e.target.value });
                setCurrentPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.componentCode") || "構件編號"}
            </div>
            <Input
              placeholder="搜尋構件編號"
              value={filters.componentCode || ""}
              onChange={(e) => {
                setModalFilters({ ...filters, componentCode: e.target.value });
                setCurrentPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.distinction") || "區別"}
            </div>
            <Input
              placeholder="搜尋區別"
              value={filters.distinction}
              onChange={(e) => {
                setModalFilters({ ...filters, distinction: e.target.value });
                setCurrentPage(1);
              }}
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.transportNumber") || "拆運號碼"}
            </div>
            <Input
              placeholder="搜尋拆運號碼"
              value={filters.transportNumber}
              onChange={(e) => {
                setModalFilters({
                  ...filters,
                  transportNumber: e.target.value,
                });
                setCurrentPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.metricSizeMark") || "斷面規格"}
            </div>
            <Input
              placeholder="搜尋斷面規格"
              value={filters.metricSizeMark}
              onChange={(e) => {
                setModalFilters({ ...filters, metricSizeMark: e.target.value });
                setCurrentPage(1);
              }}
              allowClear
            />
          </div>
        </div>
        <div
          className="mb-4 flex items-start gap-4 px-4 py-3"
          style={{ backgroundColor: "#F5F5F5", borderRadius: "8px" }}
        >
          <span style={{ fontWeight: 500, minWidth: "80px" }}>
            {t("textUpdateNode.showColumns") || "顯示欄位"}:
          </span>
          <div className="flex flex-wrap gap-3">
            {[...bomColumnOptions, ...workColumnOptions].map((col) => (
              <Checkbox
                key={col.key}
                checked={!hiddenColumns.includes(col.key)}
                onChange={(e) => toggleColumn(col.key, e.target.checked)}
              >
                {col.label}
              </Checkbox>
            ))}
          </div>
        </div>
        <div className="flex w-full flex justify-center items-center text-center">
          {/* <Table className="w-[700px]" columns={columns} dataSource={tempData} /> */}
          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBg: "#fafafa",
                  headerColor: "#1f1f1f",
                  borderColor: "#f0f0f0",
                },
              },
            }}
          >
          <Table
            className="w-full min-w-0"
            size="middle"
            scroll={{ x: "max-content" }}
            rowKey="documentId"
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredSteelAssemblyDetails?.length || 0,
              onChange: (page) => {
                setCurrentPage(page);
              },
              onShowSizeChange: (_, size) => {
                setPageSize(size);
                setCurrentPage(1);
              },
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            dataSource={filteredSteelAssemblyDetails}
            bordered
          />
          </ConfigProvider>
        </div>
        <HistoryModal
          open={historyOpen}
          row={historyRow}
          task={historyTask}
          workLabel={historyLabel}
          onClose={() => setHistoryOpen(false)}
        />
      </Modal>
      <WorkItemDialog
        open={isSecondModalOpen}
        mode="create"
        employees={employees.map((emp) => ({ id: emp.id, name: emp.name }))}
        confirmLoading={isCreatingWorkItem}
        onClose={() => setIsSecondModalOpen(false)}
        onSubmit={handleSubmit}
      />
      {/*
      <Modal
        open={isSecondModalOpen}
        onCancel={() => setIsSecondModalOpen(false)}
        width={600}
        footer={[
          <Button
            key="cancel"
            variant="default"
            style={{ marginRight: "10px" }}
            onClick={() => setIsSecondModalOpen(false)}
          >
            {t("textUpdateNode.cancel") || "取消"}
          </Button>,
          <Button key="submit" variant="primary" onClick={handleSubmit}>
            {t("textUpdateNode.confirmAdd") || "確定新增"}
          </Button>,
        ]}
      >
        <div className="py-3 text-2xl font-bold">
          {t("textUpdateNode.addNewWorkItem") || "新增工項"}
        </div>

        <div className="flex">
          <div className="py-4 pl-2">
            <div style={{ color: "#6F6F6F" }}>
              {t("textUpdateNode.workItemSelection") || "工項選擇"}
            </div>
            <Input
              placeholder={
                t("textUpdateNode.enterWorkItemName") || "請輸入工項名稱"
              }
              value={workItemForm.name}
              onChange={(e) =>
                setWorkItemForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
        </div>
        <div className="py-4 pl-2">
          <div style={{ color: "#6F6F6F" }}>
            {t("textUpdateNode.personnelSelection") || "人員選擇"}
          </div>
          <Select
            mode="multiple"
            placeholder={t("textUpdateNode.selectPersonnel") || "請選擇人員"}
            value={workItemForm.employees}
            style={{ minWidth: "200px" }}
            options={employees.map((emp) => ({
              label: emp.name,
              value: emp.id,
            }))}
            onChange={(value) =>
              setWorkItemForm((prev) => ({ ...prev, employees: value }))
            }
          />
        </div>
        <div className="py-4 pl-2">
          <div style={{ color: "#6F6F6F" }}>
            {t("textUpdateNode.scanPassCount") || "掃描通過支數"}
          </div>
          <Select
            value={workItemForm.scanMode}
            options={[
              {
                label: t("textUpdateNode.multiple") || "複數",
                value: "multiple",
              },
              { label: t("textUpdateNode.single") || "單支", value: "single" },
              { label: t("textUpdateNode.all") || "全部", value: "all" },
            ]}
            onChange={(value) =>
              setWorkItemForm((prev) => ({ ...prev, scanMode: value }))
            }
            placeholder={
              t("textUpdateNode.selectScanPassCount") || "請選擇掃描通過支數"
            }
          />
        </div>
      </Modal>
      */}
      <Modal
        open={isAddSteelAssembly}
        onCancel={() => {
          setIsAddSteelAssembly(false);
          setModalFiltersState({
            guandaId: "",
            componentCode: "",
            distinction: "",
            metricSizeMark: "",
            transportNumber: "",
          });
        }}
        style={{ minWidth: "1050px", width: "100%" }}
        footer={[
          <Button
            key="cancel"
            variant="default"
            style={{ marginRight: "10px" }}
            onClick={() => {
              setIsAddSteelAssembly(false);
              setModalFiltersState({
                guandaId: "",
                componentCode: "",
                distinction: "",
                metricSizeMark: "",
                transportNumber: "",
              });
            }}
          >
            {t("textUpdateNode.cancel") || "取消"}
          </Button>,
          <Button
            key="submit"
            variant="primary"
            onClick={() => handleSubmitAddSteelAssembly()}
          >
            {t("textUpdateNode.confirmAdd") || "確定新增"}
          </Button>,
        ]}
      >
        <div className="py-3 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
          {t("textUpdateNode.selectComponents") || "選取構件"}
        </div>
        <div className="mb-4 grid grid-cols-5 gap-4">
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.guandaId") || "廣達ID"}
            </div>
            <Input
              placeholder="搜尋廣達ID"
              value={modalFilters.guandaId}
              onChange={(e) => {
                setModalFiltersState({
                  ...modalFilters,
                  guandaId: e.target.value,
                });
                setModalCurrentPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.componentCode") || "構件編號"}
            </div>
            <Input
              placeholder="搜尋構件編號"
              value={modalFilters.componentCode || ""}
              onChange={(e) => {
                setModalFiltersState({
                  ...modalFilters,
                  componentCode: e.target.value,
                });
                setModalCurrentPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.distinction") || "區別"}
            </div>
            <Input
              placeholder="搜尋區別"
              value={modalFilters.distinction}
              onChange={(e) => {
                setModalFiltersState({
                  ...modalFilters,
                  distinction: e.target.value,
                });
                setModalCurrentPage(1);
              }}
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.transportNumber") || "拆運號碼"}
            </div>
            <Input
              placeholder="搜尋拆運號碼"
              value={modalFilters.transportNumber}
              onChange={(e) => {
                setModalFiltersState({
                  ...modalFilters,
                  transportNumber: e.target.value,
                });
                setModalCurrentPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <div className="mb-2 text-sm font-medium text-gray-700">
              {t("textUpdateNode.metricSizeMark") || "斷面規格"}
            </div>
            <Input
              placeholder="搜尋斷面規格"
              value={modalFilters.metricSizeMark}
              onChange={(e) => {
                setModalFiltersState({
                  ...modalFilters,
                  metricSizeMark: e.target.value,
                });
                setModalCurrentPage(1);
              }}
              allowClear
            />
          </div>
        </div>
        <div style={{ fontSize: "14px", color: "#333", marginBottom: "8px" }}>
          本專案共 {modalTotalItems} 隻，已選擇 {selectedBOMRow.length} 隻，總重量{" "}
          {selectedBOMRow
            .reduce((sum: number, item: any) => {
              const weight = parseFloat(item?.totalWeight) || 0;
              return sum + weight;
            }, 0)
            .toFixed(2)}{" "}
          kg
        </div>
        <Table
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          scroll={{ x: "max-content" }}
          columns={baseColumns}
          dataSource={modalDesignBOMData}
          loading={modalLoading}
          pagination={{
            pageSize: modalPageSize,
            total: modalTotalItems,
            current: modalCurrentPage,
            onChange: handleModalPageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowKey="documentId"
          bordered
        />
      </Modal>
      <Modal
        open={isManualAddSteelAssembly}
        onCancel={() => setIsManualAddSteelAssembly(false)}
        width={800}
        title={t("textUpdateNode.addNewComponent") || "新增構件"}
        footer={[
          <Button
            key="cancel"
            variant="default"
            style={{ marginRight: "10px" }}
            onClick={() => setIsManualAddSteelAssembly(false)}
          >
            {t("textUpdateNode.cancel") || "取消"}
          </Button>,
          <Button
            key="submit"
            variant="primary"
            onClick={handleManualAddSteelAssembly}
          >
            {t("textUpdateNode.confirmAdd") || "確定新增"}
          </Button>,
        ]}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-2">
              {t("textUpdateNode.componentId") || "構件ID"}
            </div>
            <Input
              value={newSteelAssembly.steelAssemblyId}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  steelAssemblyId: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterComponentId") || "請輸入構件ID"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.componentCode") || "構件編號"}
            </div>
            <Input
              value={newSteelAssembly.steelAssemblyCode}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  steelAssemblyCode: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterComponentCode") || "請輸入構件編號"
              }
            />
          </div>
          {/* <div>
            <div className="mb-2">
              {t("textUpdateNode.guandaId") || "廣達ID"}
            </div>
            <Input
              value={newSteelAssembly.guandaId}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  guandaId: e.target.value,
                }))
              }
              placeholder={t("textUpdateNode.enterGuandaId") || "請輸入管達ID"}
            />
          </div> */}
          <div>
            <div className="mb-2">
              {t("textUpdateNode.installationLocation") || "安裝位置"}
            </div>
            <Input
              value={newSteelAssembly.installationLocation}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  installationLocation: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterInstallationLocation") ||
                "請輸入安裝位置"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.installationElevation") || "安裝高程"}
            </div>
            <Input
              value={newSteelAssembly.installationElevation}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  installationElevation: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterInstallationElevation") ||
                "請輸入安裝高程"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.distinction") || "區別"}
            </div>
            <Input
              value={newSteelAssembly.distinction}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  distinction: e.target.value,
                }))
              }
              placeholder={t("textUpdateNode.enterDistinction") || "請輸入區別"}
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.transportNumber") || "拆運號碼"}
            </div>
            <Input
              value={newSteelAssembly.transportNumber}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  transportNumber: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterTransportNumber") || "請輸入拆運號碼"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.transportDescription") || "拆運說明"}
            </div>
            <Input
              value={newSteelAssembly.transportDescription}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  transportDescription: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterTransportDescription") ||
                "請輸入拆運說明"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.drawingName") || "圖面名稱"}
            </div>
            <Input
              value={newSteelAssembly.drawingName}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  drawingName: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterDrawingName") || "請輸入圖面名稱"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.totalWidth") || "總寬度"}
            </div>
            <Input
              value={newSteelAssembly.totalWidth}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  totalWidth: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterTotalWidth") || "請輸入總寬度"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.totalHeight") || "總高度"}
            </div>
            <Input
              value={newSteelAssembly.totalHeight}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  totalHeight: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterTotalHeight") || "請輸入總高度"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.totalLength") || "總長度"}
            </div>
            <Input
              value={newSteelAssembly.totalLength}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  totalLength: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterTotalLength") || "請輸入總長度"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.totalWeight") || "總重量"}
            </div>
            <Input
              value={newSteelAssembly.totalWeight}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  totalWeight: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterTotalWeight") || "請輸入總重量"
              }
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.totalArea") || "總面積"}
            </div>
            <Input
              value={newSteelAssembly.totalArea}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  totalArea: e.target.value,
                }))
              }
              placeholder={t("textUpdateNode.enterTotalArea") || "請輸入總面積"}
            />
          </div>
          <div>
            <div className="mb-2">
              {t("textUpdateNode.metricSizeMark") || "斷面規格"}
            </div>
            <Input
              value={newSteelAssembly.metricSizeMark}
              onChange={(e) =>
                setNewSteelAssembly((prev) => ({
                  ...prev,
                  metricSizeMark: e.target.value,
                }))
              }
              placeholder={
                t("textUpdateNode.enterMetricSizeMark") || "請輸入斷面規格"
              }
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={qrCodePopup.visible}
        onCancel={() => setQrCodePopup({ visible: false, guandaId: null, code: null })}
        footer={null}
        title={t("textUpdateNode.guandaId") || "廣達ID"}
        width={240}
      >
        <div className="flex flex-col items-center py-4">
          <QRCode
            value={String(qrCodePopup.guandaId || "0").padStart(7, "0")}
            size={160}
          />
          <div className="mt-3 text-center">
            <div className="text-base font-medium">
              {String(qrCodePopup.guandaId || "0").padStart(7, "0")}
            </div>
            {qrCodePopup.code && (
              <div className="text-sm text-gray-500">{qrCodePopup.code}</div>
            )}
          </div>
        </div>
      </Modal>
      {ifc3d.ifcPreview}
    </>
  );
}

export default TextUpdaterNode;
